import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import { FileText, Plus, Trash2, FolderOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ReportTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  template_data: any;
  usage_count: number;
  created_at: string;
}

interface ReportTemplatesManagerProps {
  onSelectTemplate: (template: ReportTemplate) => void;
}

export default function ReportTemplatesManager({ onSelectTemplate }: ReportTemplatesManagerProps) {
  const { getToken } = useAuth();
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
    loadCategories();
  }, [selectedCategory]);

  const loadTemplates = async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const url = selectedCategory === 'all'
        ? `${BACKEND_URL}/api/v1/dashboard/templates`
        : `${BACKEND_URL}/api/v1/dashboard/templates?category=${selectedCategory}`;
      const response = await axios.get(url, { headers });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/v1/dashboard/templates/categories/list`, { headers });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${BACKEND_URL}/api/v1/dashboard/templates/${templateId}`, { headers });
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      setErrorMessage('Failed to delete template');
    }
  };

  const handleSelectTemplate = async (template: ReportTemplate) => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/v1/dashboard/templates/${template.id}`, { headers });
      onSelectTemplate(response.data);
      setShowModal(false);
    } catch (error) {
      console.error('Failed to load template:', error);
      setErrorMessage('Failed to load template');
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
      >
        <FileText className="w-4 h-4" />
        Use Template
      </button>

      {/* Templates Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Report Templates</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              {/* Category Filter */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                    selectedCategory === 'all'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Templates
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                      selectedCategory === category
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FolderOpen className="w-4 h-4 inline mr-1" />
                    {category}
                  </button>
                ))}
              </div>

              {/* Templates Grid */}
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No templates found</p>
                  <p className="text-sm text-gray-400 mt-2">Create templates by saving reports with pre-filled data</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition cursor-pointer group"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <FileText className="w-8 h-8 text-green-600" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(template.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
                      {template.description && (
                        <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
                        <span className="bg-gray-100 px-2 py-1 rounded">{template.category}</span>
                        <span>{template.usage_count} uses</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
